import { FranchiseDetailPage } from './FranchiseDetailPage'
import { FranchiseIntegrationsPage } from './FranchiseIntegrationsPage'
import { FranchiseManagerSettingsPage } from './FranchiseManagerSettingsPage'
import { FranchiseUsagePage } from './FranchiseUsagePage'
import { FranchisesPage } from './FranchisesPage'

import { NewFranchisePage } from './NewFranchisePage'

/**
 * `saas-franchises` - the Control Tower's tenants, and everything reached from one.
 *
 * **The first of `SAAS.md` §3's seven packages to exist, and the reason it is first is FB-504.** The
 * 2026-07-31 rework order objected to feature code living in `apps/super-admin/src/pages`; this is
 * that move, done as the vehicle for the fix the review found rather than as a refactor of its own.
 *
 * **Route order is load-bearing.** `wouter` matches in declaration order, so `/franchises/new` has to
 * precede `/franchises/:id` or the wizard is parsed as a franchise whose id is the word *new* - which
 * is why `App.tsx` declared them apart and why they are adjacent and ordered here instead.
 */
export const saasFranchisesModule = {
  name: 'saas-franchises',
  routes: [
    { path: '/franchises', page: 'franchises', component: FranchisesPage },
    { path: '/franchises/new', page: 'new-franchise', component: NewFranchisePage },
    { path: '/franchises/:id/usage', page: 'franchise-usage', component: FranchiseUsagePage },
    {
      path: '/franchises/:id/integrations',
      page: 'franchise-integrations',
      component: FranchiseIntegrationsPage,
    },
    {
      path: '/franchises/:id/manager-settings',
      page: 'franchise-manager-settings',
      component: FranchiseManagerSettingsPage,
    },
    { path: '/franchises/:id', page: 'franchise-detail', component: FranchiseDetailPage },
  ],
} as const

export { FranchiseDetailPage } from './FranchiseDetailPage'
export { FranchiseIntegrationsPage } from './FranchiseIntegrationsPage'
export { FranchiseManagerSettingsPage } from './FranchiseManagerSettingsPage'
export { FranchiseUsagePage } from './FranchiseUsagePage'
export { FranchisesPage } from './FranchisesPage'
export { NewFranchisePage } from './NewFranchisePage'

export { franchiseRows, matchesFranchiseSearch, primaryDomainOf } from './FranchisesPage'

// SAAS.md §3.87 - the two shapes the column ratchet compares against the server's own `EDITABLE`
// map. Exported so the check can live in the app, which is where a test may read the filesystem.
export { draftOfFranchise, type FranchiseDraft } from './franchise-detail'
export { EMPTY_DRAFT, createPayload, type NewFranchiseDraft } from './new-franchise'
// The row type, for the Tower's dashboard list - §3.52. The parser and the domain rule above were
// already exported; only the type was not, and re-exporting them again is what the build caught.
export type { FranchiseRow } from './FranchisesPage'

/*
 * SAAS.md §3.184 - the partner rules, moved here from `apps/super-admin/src/pages/partners.ts`.
 *
 * **A whole re-export rather than a named list, and the reason is honesty about what changed.** Every one of
 * those thirty symbols was already the app's public surface in practice - `PartnersPage.tsx`,
 * `profitability.ts` and two test files import them - so naming them one by one would read as a decision
 * about which to expose when none was made. §3.155's *"widening a module's public surface to satisfy a guard
 * is the wrong trade"* is about reaching for one internal helper from a test; this file is a rules module
 * whose exports were the boundary already.
 *
 * **Why they moved at all**: `FranchiseDetailPage` draws a franchise's partner shares (§3.184) and a module
 * cannot import from the app that depends on it. Spelling `sharesOf` and `isOverAllocated` twice is
 * `agos-one-guard-spelled-twice` on a money rule, and §3.156 is the entry where two readings of one share
 * list let the over-allocation guard disagree with the figure printed beside it.
 */
export * from './partners'
